/*
 * Planigo
 * Auteur  : Stéphane Raverdy
 * Société : SCIAD 2026
 */
import { useEffect, useMemo, useState } from 'react';
import { documentTemplatesApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { FileText, Plus } from 'lucide-react';

const KIND_LABELS = {
  intervention: "Bon d'intervention",
  sav_return: 'Bon de retour SAV',
  meeting_report: 'Compte rendu de réunion',
};

const PREVIEW_VALUES = {
  intervention: { intervention_code:'INT-26-00125', intervention_title:'Remplacement poste utilisateur', requester_name:'Client Démo', technician_name:'Technicien IT', site:'Saint-Agrève', asset:'PC portable - IT-00452', status:'Terminée', description:'Diagnostic et remplacement du matériel défaillant.' },
  sav_return: { sav_code:'SAV-26-0042', asset:'PC portable - IT-00452', supplier:'Fournisseur Démo', status:'En réparation', problem:'Matériel retourné pour diagnostic.' },
  meeting_report: { project:'PRJ-26-0018 - Migration SI', meeting_title:'Comité projet', meeting_date:'22/10/2026', participants:'Équipe projet', decisions:'Validation du planning de déploiement.', actions:'Préparer les tests métier.' },
};

function renderPreview(text, kind) {
  const values = PREVIEW_VALUES[kind] || {};
  return String(text || '').replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, key) => values[key] ?? `[${key}]`);
}

export default function DocumentTemplatesAdminPanel() {
  const [rows, setRows] = useState([]);
  const [variables, setVariables] = useState({});
  const [kind, setKind] = useState('intervention');
  const [selectedId, setSelectedId] = useState('new');
  const [form, setForm] = useState({ name: '', title_template: '', body_template: '', active: true, is_default: true });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await documentTemplatesApi.getAll();
      setRows(data.items || []);
      setVariables(data.variables || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Chargement des modèles impossible');
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => rows.filter(r => r.kind === kind), [rows, kind]);

  const changeKind = (value) => {
    setKind(value); setSelectedId('new');
    setForm({ name: '', title_template: '', body_template: '', active: true, is_default: true });
  };
  const select = (id) => {
    setSelectedId(id);
    if (id === 'new') return setForm({ name: '', title_template: '', body_template: '', active: true, is_default: false });
    const row = rows.find(r => r.id === id);
    if (row) setForm({ name: row.name || '', title_template: row.title_template || '', body_template: row.body_template || '', active: row.active !== false, is_default: !!row.is_default });
  };
  const save = async () => {
    if (!form.name.trim()) return toast.error('Nom obligatoire');
    setBusy(true);
    try {
      const payload = { kind, ...form };
      if (selectedId === 'new') await documentTemplatesApi.create(payload);
      else await documentTemplatesApi.update(selectedId, payload);
      toast.success('Modèle enregistré');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Enregistrement impossible'); }
    finally { setBusy(false); }
  };
  const addVariable = (name) => {
    const token = `{{${name}}}`;
    setForm(p => ({ ...p, body_template: `${p.body_template}${p.body_template ? '\n' : ''}${token}` }));
  };

  return <Card className="card-surface">
    <CardHeader className="py-3 px-4"><CardTitle className="text-sm flex items-center gap-2"><FileText className="w-4 h-4"/>Modèles de documents</CardTitle></CardHeader>
    <CardContent className="px-4 pb-4 space-y-4">
      <p className="text-xs text-slate-500">Personnalise les bons d'intervention, bons de retour SAV et comptes rendus. Les variables sont remplacées lors de la génération Word/PDF.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><Label>Type de document</Label><Select value={kind} onValueChange={changeKind}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent>{Object.entries(KIND_LABELS).map(([k,v])=><SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
        <div><Label>Modèle</Label><Select value={selectedId} onValueChange={select}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent><SelectItem value="new">+ Nouveau modèle</SelectItem>{filtered.map(r=><SelectItem key={r.id} value={r.id}>{r.name}{r.is_default?' (défaut)':''}</SelectItem>)}</SelectContent></Select></div>
      </div>
      <div><Label>Nom</Label><Input value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="Ex. Bon SAV Planigo"/></div>
      <div><Label>Titre</Label><Input value={form.title_template} onChange={e=>setForm(p=>({...p,title_template:e.target.value}))}/></div>
      <div><Label>Contenu</Label><Textarea rows={12} value={form.body_template} onChange={e=>setForm(p=>({...p,body_template:e.target.value}))}/></div>
      <div>
        <Label>Aperçu du modèle</Label>
        <div className="mt-1 rounded-xl border bg-slate-100 p-3">
          <div className="mx-auto max-w-3xl min-h-[220px] rounded-lg border bg-white p-6 shadow-sm">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">{KIND_LABELS[kind]}</div>
            <h3 className="text-lg font-bold text-slate-900 mb-4">{renderPreview(form.title_template || form.name || KIND_LABELS[kind], kind)}</h3>
            <div className="whitespace-pre-wrap text-sm leading-6 text-slate-700">{renderPreview(form.body_template || 'Le contenu du modèle apparaîtra ici.', kind)}</div>
          </div>
        </div>
        <p className="mt-1 text-[11px] text-slate-500">Aperçu uniquement : les données fictives ne sont jamais enregistrées dans le modèle.</p>
      </div>
      <div><Label className="text-xs">Variables disponibles</Label><div className="flex flex-wrap gap-1 mt-1">{(variables[kind]||[]).map(v=><Button key={v} type="button" size="sm" variant="outline" className="h-7 text-[11px]" onClick={()=>addVariable(v)}>{`{{${v}}}`}</Button>)}</div></div>
      <div className="flex flex-wrap gap-5"><label className="flex items-center gap-2 text-sm"><Switch checked={form.active} onCheckedChange={v=>setForm(p=>({...p,active:!!v}))}/>Actif</label><label className="flex items-center gap-2 text-sm"><Switch checked={form.is_default} onCheckedChange={v=>setForm(p=>({...p,is_default:!!v}))}/>Modèle par défaut</label></div>
      <div className="flex justify-end"><Button disabled={busy} onClick={save}>{selectedId==='new'?<Plus className="w-4 h-4 mr-1"/>:null}{busy?'Enregistrement...':'Enregistrer'}</Button></div>
    </CardContent>
  </Card>;
}
